import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import HierarchicalOpalGrid from '../DenaliDataPipeline/report/HierarchicalOpalGrid'
import { useHideIfNoDataWhen } from '../HideIfNoData'
import BlueprintTableToolbar from './BlueprintTableToolbar'
import useBlueprintData from './hooks/useBlueprintData'
import useExhaustiveBlueprintData, { MAX_ROWS } from './hooks/useExhaustiveBlueprintData'
import { LATCH_LATEST } from './constants'
import useBlueprintColumnDefs, { mapDefaultSort } from './hooks/useBlueprintColumnDefs'
import useColumnConfig from './hooks/useColumnConfig'
import useBlueprintTreeData from './hooks/useBlueprintTreeData'
import GridColumnConfigModal from './GridColumnConfigModal'
import BlueprintExportButton from './BlueprintExportButton'
import useStyleVariant from './hooks/useStyleVariant'

const PAGE_STATE = { pageIndex: 0, pageSize: MAX_ROWS }

function getSubRows (row) {
  return row.subRows
}

/**
 * Blueprint-driven hierarchical table. Fetches flat data from a single purpose,
 * transforms it into a tree via composite-key grouping, and renders a read-only
 * expandable table. Supports three aggregation modes: none, client, and server.
 */
function BlueprintHierarchicalTable ({
  blueprintCode,
  config,
  latch,
  displayName,
  parameters,
  scopePredicates
}) {
  const {
    purposeCode,
    footerPurposeCode,
    footerColumnMapping,
    defaultSort: rawDefaultSort,
    hierarchy
  } = config

  const effectiveParameters = useMemo(() => {
    if (!latch) return parameters
    return { ...parameters, [latch.parameterName]: LATCH_LATEST }
  }, [parameters, latch])

  const hasFooterPurpose = !!footerPurposeCode && footerPurposeCode !== purposeCode

  const hasColumnConfig = !!config.enableColumnConfig
  const hasSearch = !!config.enableSearch
  const { mergedColumns, handleColumnConfigSave } = useColumnConfig(
    config.columns, config.localStorageKey
  )
  const [configOpen, setConfigOpen] = useState(false)
  const [globalFilter, setGlobalFilter] = useState('')

  const { data: mainData, isLoading: mainLoading, truncated } = useExhaustiveBlueprintData(
    blueprintCode,
    purposeCode,
    { parameters: effectiveParameters, pageSize: 1000, scopePredicates }
  )

  const { data: footerData, isLoading: footerLoading } = useBlueprintData(
    blueprintCode,
    hasFooterPurpose ? footerPurposeCode : null,
    { parameters: effectiveParameters, pageSize: 1000, scopePredicates }
  )

  const effectiveColumns = hasColumnConfig ? mergedColumns : config.columns

  const { columns, includeFooter } = useBlueprintColumnDefs(
    effectiveColumns,
    footerData,
    footerColumnMapping
  )

  const defaultSort = useMemo(() => mapDefaultSort(rawDefaultSort), [rawDefaultSort])
  const [sortState, setSortState] = useState(defaultSort)

  useEffect(() => {
    setSortState(defaultSort)
  }, [defaultSort])

  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.defaultShown ?? true)
  }, [columns])

  const variant = useStyleVariant('Hierarchical Table', config)

  const { treeData, expandState, setExpandState } = useBlueprintTreeData(mainData, hierarchy)

  const isLoading = mainLoading || (hasFooterPurpose && footerLoading)
  useHideIfNoDataWhen(() => isLoading || !mainData?.length, [isLoading, mainData])
  const isFiltering = hasSearch && globalFilter.length > 0
  const effectiveExpandState = isFiltering ? true : expandState
  const showToolbar = !!config.export?.enabled || hasColumnConfig || hasSearch

  const truncationWarning = truncated
    ? (
      <div className='opal-callout opal-callout-warning font-size-sm'>
        Showing the first {mainData.length.toLocaleString()} rows. The full dataset exceeds the display limit.
      </div>
    )
    : null

  const table = (
    <HierarchicalOpalGrid
      {...variant.props}
      className={variant.tableClassName}
      style={variant.styles}
      data={treeData}
      columns={visibleColumns}
      isFetching={isLoading}
      pageState={PAGE_STATE}
      onPageChange={() => {}}
      expandState={effectiveExpandState}
      onExpandStateChange={setExpandState}
      sortState={sortState}
      onSortChange={setSortState}
      getSubRows={getSubRows}
      includeFooter={includeFooter}
      hidePaging
      enableFiltering={hasSearch}
      globalFilter={hasSearch ? globalFilter : undefined}
      onGlobalFilterChange={hasSearch ? setGlobalFilter : undefined}
    />
  )

  if (!showToolbar && !displayName && !truncated) {
    return variant.className
      ? <div className={variant.className}>{table}</div>
      : table
  }

  return (
    <BlueprintTableToolbar
      className={variant.className}
      displayName={displayName}
      hasSearch={hasSearch}
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      showExport={!!config.export?.enabled}
      exportButton={
        <BlueprintExportButton
          blueprintCode={blueprintCode}
          config={config}
          purposeCode={purposeCode}
          parameters={effectiveParameters}
          scopePredicates={scopePredicates}
        />
      }
      hasColumnConfig={hasColumnConfig}
      onConfigClick={() => setConfigOpen(true)}
      configModal={
        hasColumnConfig
          ? (
            <GridColumnConfigModal
              open={configOpen}
              onClose={() => setConfigOpen(false)}
              columns={mergedColumns}
              defaultColumns={config.columns}
              onSave={handleColumnConfigSave}
            />
          )
          : null
      }
    >
      {truncationWarning}
      {table}
    </BlueprintTableToolbar>
  )
}

BlueprintHierarchicalTable.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    purposeCode: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      hidden: PropTypes.bool,
      exclude: PropTypes.bool,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      formatString: PropTypes.string
    })),
    hierarchy: PropTypes.shape({
      levels: PropTypes.arrayOf(PropTypes.shape({
        groupBy: PropTypes.arrayOf(PropTypes.string).isRequired,
        labelAccessor: PropTypes.string.isRequired
      })).isRequired,
      defaultExpandDepth: PropTypes.number,
      aggregation: PropTypes.shape({
        mode: PropTypes.oneOf(['none', 'client', 'server']),
        columnAggregates: PropTypes.arrayOf(PropTypes.shape({
          column: PropTypes.string.isRequired,
          fn: PropTypes.string.isRequired
        })),
        depthColumn: PropTypes.string
      })
    }).isRequired,
    footerPurposeCode: PropTypes.string,
    footerColumnMapping: PropTypes.arrayOf(PropTypes.shape({
      mainColumn: PropTypes.string,
      footerColumn: PropTypes.string
    })),
    defaultSort: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      dir: PropTypes.string
    })),
    enableColumnConfig: PropTypes.bool,
    enableSearch: PropTypes.bool,
    localStorageKey: PropTypes.string,
    export: PropTypes.shape({
      enabled: PropTypes.bool,
      formats: PropTypes.arrayOf(PropTypes.string),
      defaultFileName: PropTypes.string
    })
  }).isRequired,
  latch: PropTypes.object,
  displayName: PropTypes.string,
  parameters: PropTypes.object,
  scopePredicates: PropTypes.arrayOf(PropTypes.object)
}

BlueprintHierarchicalTable.defaultProps = {
  latch: null,
  parameters: {}
}

export default BlueprintHierarchicalTable
